<script setup lang="ts">
import Icon from './Icon.vue'
import Skeleton from './Skeleton.vue'
/** Pick row used inside dialogs: title, one line of facts, selected state.
 *  Radio mark for pick-one; `multiple` draws a checkbox mark for pick-many (memory bindings, connector picks).
 *  `disabled` is a choice the contract names but does not accept yet: it reads as unavailable and cannot be picked.
 *  `placeholder` is the same row before its options have answered: the identical box with bars where the
 *  two lines will be, so a dialog opens at the height it keeps instead of growing around the answer. */
/* `facts` defaults to the empty string: the template already treats it as "no second line", so the
    render is the same as leaving it out, and every other optional string here has that shape. */
withDefaults(defineProps<{ title?: string; facts?: string; selected?: boolean; multiple?: boolean; disabled?: boolean; placeholder?: boolean }>(), { title: '', facts: '', multiple: false })
</script>

<template>
  <button
type="button" :disabled="disabled || placeholder" :aria-hidden="placeholder ? 'true' : undefined"
    class="flex items-center gap-2.5 rounded-md border px-2.5 py-2 text-left disabled:pointer-events-none disabled:opacity-45"
    :class="selected ? 'border-primary/60 bg-sidebar-accent' : 'border-border hover:bg-muted/40'">
    <span
class="flex size-3.5 shrink-0 items-center justify-center border"
          :class="[multiple ? 'rounded-[3px]' : 'rounded-full', selected ? (multiple ? 'border-primary bg-primary text-primary-foreground' : 'border-primary') : 'border-border']">
      <Icon v-if="selected && multiple" name="check" :size="10" />
      <span v-else-if="selected" class="size-1.5 rounded-full bg-primary shadow-glow-primary" />
    </span>
    <!-- The placeholder keeps both lines, because the settled row has both: a row that grew a
         second line when the answer arrived would move everything under it. -->
    <span class="flex min-w-0 flex-col">
      <template v-if="placeholder">
        <span class="flex flex-col gap-1 py-[var(--measure-2px)]">
          <Skeleton width="var(--measure-130px)" height="var(--skeleton-title)" />
          <Skeleton width="var(--measure-60px)" />
        </span>
      </template>
      <template v-else>
        <span class="truncate text-xs font-medium">{{ title }}</span>
        <span v-if="facts" class="num text-[length:var(--type-fine)] text-muted-foreground">{{ facts }}</span>
      </template>
    </span>
    <slot />
  </button>
</template>
